/**
 * Credits Purchase Dialog
 *
 * In-app PAYG credits purchase modal so users who hit their included
 * message limit can buy credits without leaving chat for /api-credits.
 *
 * Primary responsibilities:
 * - Open on the `losi:open-credits-purchase` window event (same pattern
 *   as PlanPicker's `losi:open-paywall`)
 * - Load balance + packages from GET /api/v1/credits
 * - Start Stripe Checkout via POST /api/v1/credits, returning to the
 *   current page afterwards (return_path)
 * - Toast purchase success/cancel when Stripe returns with query params
 *
 * Dependencies:
 * - components/ui/dialog, button, input, label, badge
 * - components/ui/api-credit-display
 * - context/context (paywall fallback for free-plan users)
 */

'use client'

import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { IconCreditCard, IconLoader, IconPlus, IconShoppingCart } from '@tabler/icons-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import ApiCreditDisplay from '@/components/ui/api-credit-display'
import { ChatbotUIContext } from '@/context/context'

export const OPEN_CREDITS_PURCHASE_EVENT = 'losi:open-credits-purchase'

// Dispatch from anywhere in the app shell to open the purchase modal
// instead of navigating to the standalone /api-credits page.
export function openCreditsPurchaseDialog() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_CREDITS_PURCHASE_EVENT))
}

interface ApiCreditPackage {
  id: string
  name: string
  credits: number
  price_usd: number
}

interface CreditsData {
  balance: number
  is_admin: boolean
  packages: ApiCreditPackage[]
}

export function CreditsPurchaseDialog() {
  const { t } = useTranslation()
  const { setIsPaywallOpen } = useContext(ChatbotUIContext)

  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [creditsData, setCreditsData] = useState<CreditsData | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [needsPaidPlan, setNeedsPaidPlan] = useState(false)
  const [customAmount, setCustomAmount] = useState('')
  const [purchasingId, setPurchasingId] = useState<string | null>(null)

  // Open when any surface (limit toast, profile menu, ...) asks for credits.
  useEffect(() => {
    const handleOpen = () => setOpen(true)
    window.addEventListener(OPEN_CREDITS_PURCHASE_EVENT, handleOpen)
    return () => window.removeEventListener(OPEN_CREDITS_PURCHASE_EVENT, handleOpen)
  }, [])

  // Stripe returns to the page the purchase started from (return_path).
  // Surface the outcome here since this component is always mounted in
  // the app shell, then strip the params so refreshes don't re-toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session_id')
    const canceled = params.get('canceled')
    if (!sessionId && !canceled) return

    if (sessionId) {
      toast.success(t('Purchase Successful'), {
        description: t('Your PAYG credits have been added to your account.')
      })
    } else {
      toast.error(t('Purchase Canceled'), {
        description: t('Your PAYG credits purchase was canceled.')
      })
    }

    params.delete('session_id')
    params.delete('canceled')
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    )
  }, [t])

  const fetchCreditsData = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    setNeedsPaidPlan(false)
    try {
      const response = await fetch('/api/v1/credits')

      if (!response.ok) {
        const error = await response.json().catch(() => null)
        if (response.status === 403) {
          setNeedsPaidPlan(true)
          return
        }
        throw new Error(error?.error?.message || 'Failed to fetch PAYG credits data')
      }

      const data = await response.json()
      // Normalize defensively: render reads these unconditionally.
      setCreditsData({
        balance: data.balance ?? 0,
        is_admin: data.is_admin ?? false,
        packages: Array.isArray(data.packages) ? data.packages : []
      })
    } catch (error: any) {
      console.error('Error fetching PAYG credits:', error)
      setLoadError(error.message || t('Failed to fetch PAYG credits data'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (open) {
      void fetchCreditsData()
    }
  }, [open, fetchCreditsData])

  // Calculate custom credits amount with bonus (mirrors /api-credits)
  const calculateCustomCredits = (amount: string) => {
    const numAmount = parseFloat(amount)
    if (isNaN(numAmount)) return 0

    // 10% bonus for amounts over $100
    return numAmount * (numAmount >= 100 ? 1.1 : 1.0)
  }

  const customAmountValid =
    customAmount !== '' &&
    !isNaN(parseFloat(customAmount)) &&
    parseFloat(customAmount) >= 5 &&
    parseFloat(customAmount) <= 1000

  const purchaseCredits = async (payload: { package_id?: string; custom_amount?: number }) => {
    setPurchasingId(payload.package_id ?? 'custom')
    try {
      const response = await fetch('/api/v1/credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...payload,
          // Come back to the page the user bought from (e.g. chat)
          // instead of the standalone credits page.
          return_path: window.location.pathname
        })
      })

      if (!response.ok) {
        const error = await response.json().catch(() => null)
        // Session expired (or missing on this domain) while the app
        // shell stayed open — send the user to login and bring them
        // back here instead of failing with a cryptic toast.
        if (response.status === 401) {
          window.location.href = `/login?next=${encodeURIComponent(
            window.location.pathname.replace(/^\//, '')
          )}`
          return
        }
        throw new Error(error?.error?.message || 'Failed to create checkout session')
      }

      const data = await response.json()

      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url
      } else {
        throw new Error('No checkout URL returned')
      }
    } catch (error: any) {
      console.error('Error purchasing credits:', error)
      toast.error(t('Error'), {
        description: error.message || t('Failed to purchase PAYG credits')
      })
      setPurchasingId(null)
    }
  }

  const isPurchasing = purchasingId !== null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>{t('Purchase Credits')}</DialogTitle>
          <DialogDescription>
            {t('PAYG credits keep paid chat going after included messages run out')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 px-6 pb-6">
          {isLoading && !creditsData && !needsPaidPlan && (
            <div className="flex flex-col items-center justify-center py-10">
              <IconLoader size={32} className="text-primary mb-3 animate-spin" />
              <p className="text-muted-foreground text-sm">{t('Loading PAYG credits data...')}</p>
            </div>
          )}

          {loadError && (
            <div className="rounded-lg border p-4 text-center">
              <p className="text-muted-foreground mb-3 text-sm">{loadError}</p>
              <Button variant="outline" onClick={() => void fetchCreditsData()}>
                {t('Try Again')}
              </Button>
            </div>
          )}

          {needsPaidPlan && (
            <div className="rounded-lg border p-4 text-center">
              <p className="text-muted-foreground mb-4 text-sm">
                {t('PAYG credits are available on paid plans and Developer Mode accounts.')}
              </p>
              <Button
                onClick={() => {
                  setOpen(false)
                  setIsPaywallOpen(true)
                }}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {t('View Plans')}
              </Button>
            </div>
          )}

          {creditsData && (
            <>
              <div className="bg-muted/50 flex items-center justify-between rounded-lg border px-4 py-3">
                <ApiCreditDisplay balance={creditsData.balance} size="sm" showProgress={false} />
                {creditsData.is_admin && (
                  <Badge variant="outline">{t('Platform Admin (Unlimited)')}</Badge>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {creditsData.packages.map((pkg) => (
                  <div key={pkg.id} className="rounded-lg border p-4 shadow-sm">
                    <p className="font-medium">{pkg.name}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('{{credits}} PAYG credits', { credits: pkg.credits.toFixed(2) })}
                    </p>
                    <p className="mt-2 text-xl font-bold">${pkg.price_usd.toFixed(2)}</p>
                    <p className="text-muted-foreground text-xs">
                      ${(pkg.price_usd / pkg.credits).toFixed(3)} {t('per credit')}
                    </p>
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90 mt-3 w-full"
                      onClick={() => void purchaseCredits({ package_id: pkg.id })}
                      disabled={isPurchasing}
                    >
                      {purchasingId === pkg.id ? (
                        <IconLoader className="mr-2 size-4 animate-spin" />
                      ) : (
                        <IconShoppingCart size={16} className="mr-2" />
                      )}
                      {t('Purchase')}
                    </Button>
                  </div>
                ))}
              </div>

              <div className="rounded-lg border border-dashed p-4">
                <p className="font-medium">{t('Custom Amount')}</p>
                <p className="text-muted-foreground text-sm">{t('Choose your own credit amount')}</p>

                <div className="mt-3 flex items-center gap-3">
                  <Label htmlFor="credits-custom-amount" className="shrink-0">
                    {t('Amount ($)')}
                  </Label>
                  <Input
                    id="credits-custom-amount"
                    type="number"
                    min="5"
                    max="1000"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="25.00"
                  />
                </div>

                {customAmount && !isNaN(parseFloat(customAmount)) && (
                  <div className="mt-3 flex items-center gap-2">
                    <ApiCreditDisplay
                      balance={calculateCustomCredits(customAmount)}
                      showProgress={false}
                      size="sm"
                    />
                    {parseFloat(customAmount) >= 100 && (
                      <Badge variant="outline">{t('+10% Bonus')}</Badge>
                    )}
                  </div>
                )}

                <p className="text-muted-foreground mt-2 text-xs">
                  {t('Minimum $5, maximum $1000. Get a 10% bonus when you add $100 or more.')}
                </p>

                <Button
                  variant="outline"
                  className="hover:bg-secondary mt-3 w-full"
                  onClick={() =>
                    void purchaseCredits({ custom_amount: parseFloat(customAmount) })
                  }
                  disabled={isPurchasing || !customAmountValid}
                >
                  {purchasingId === 'custom' ? (
                    <IconLoader className="mr-2 size-4 animate-spin" />
                  ) : (
                    <IconPlus size={16} className="mr-2" />
                  )}
                  {t('Purchase')}
                </Button>
              </div>

              <p className="text-muted-foreground flex items-center gap-2 text-xs">
                <IconCreditCard size={14} />
                {t('Secure checkout by Stripe. Credits are added as soon as payment completes.')}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
